import React from 'react';
import { Check, X, Info, ShieldCheck } from 'lucide-react';

/**
 * ComparisonTable — „Mit FuelX Card / Ohne"-Gegenüberstellung.
 *
 * Zwei Modi:
 *   - variant="private" (Homepage-Autofahrer, Default)
 *   - variant="business" (Business-Landing, Fuhrpark)
 *
 * Design: Editorial, stone-Palette, dezente emerald-Akzente auf „Mit FuelX"-Spalte.
 * Content verstärkt: konkrete Kostenbeträge und %-Vergleiche (v2 · Feb 2026).
 * InfoBox (Klarstellung Rolle als Lead-Intermediär) unter der Tabelle.
 */
const PRIVATE_ROWS = [
  {
    topic: 'Ein zentraler Ort für dein Auto',
    without: '10 Apps · 10 Logins · 10 Rechnungen · nichts spricht miteinander',
    with: 'Eine Plattform — Versicherung, TÜV, Werkstatt, Reifen, Sprit, Termine · zusammen im Dashboard',
  },
  {
    topic: 'Jemand kümmert sich',
    without: '„Ich muss noch schnell den TÜV verlängern …" — vergessen, teuer, stressig',
    with: 'FuelX-Concierge steuert dein Auto — persönlicher Ansprechpartner, deutsch, in Stuttgart',
  },
  {
    topic: 'Automatische Erinnerungen',
    without: 'Selber im Kalender nachhalten · verpasste TÜV-Fristen kosten bis 60 € Strafe',
    with: 'TÜV · Ölwechsel · Reifenwechsel · Versicherungsprüfung — E-Mail & App-Push zur richtigen Zeit',
  },
  {
    topic: 'KFZ-Prämie zurückholen',
    without: 'Prämien-Rückerstattung? Nie beantragt · Ø 300–800 € pro Jahr liegen ungenutzt',
    with: 'FuelX prüft & holt die KFZ-Prämie aktiv zurück · Beitrag landet auf deinem Konto',
  },
  {
    topic: 'KFZ-Kosten dauerhaft senken',
    without: 'Jedes Jahr das Gleiche · niemand prüft ob günstiger geht',
    with: 'Kontinuierliche Optimierung: Sprit, Versicherung, Werkstatt, Kredit · immer nur bei Ersparnis',
  },
  {
    topic: 'Hotel buchen mit Bestpreis',
    without: 'Zwischen 5 Portalen suchen · Bestpreis nie sicher · Reise-Chaos',
    with: '1,6 Mio Hotels · Bestpreis-Garantie · persönlicher Ansprechpartner · FuelGood-Guthaben zurück',
  },
  {
    topic: 'Tanken',
    without: 'Voller Literpreis · Ø 1,85 €/L · nichts kommt zurück',
    with: 'FuelGood Guthaben pro km · Ø 0,40 €/L Effektiv-Rabatt',
  },
  {
    topic: 'Kfz-Versicherung',
    without: '„Vielleicht ist mein Beitrag noch marktgerecht — seit 4 Jahren keine Prüfung"',
    with: 'KI-Vergleich · Angebot nur bei Ersparnis · direkt vom Versicherer',
  },
  {
    topic: 'TÜV / HU',
    without: '160 € alle 24 Monate · Pflicht · zahlst du komplett selbst',
    with: 'Bis 160 € über FuelX Hotels als Guthaben zurück',
  },
  {
    topic: 'Werkstattkosten-Check',
    without: '„Da wäre noch etwas …" · Ø 200 € Zusatzposten · du zahlst ungeprüft',
    with: 'Kostenlose Fehlerdiagnose + transparente Reparaturkosten-Prüfung mit Werkstätten',
  },
  {
    topic: 'Reifenwechsel',
    without: '2× pro Jahr · Ø 500 € pro Saison · jedes Mal neu bezahlen',
    with: 'Bis 250 € pro Saison auf Prepaid Card zurück',
  },
  {
    topic: 'Kfz-Steuer',
    without: 'Jedes Jahr im Januar Ø 300 € weg · keine Optimierung möglich',
    with: 'Bis 450 € über FuelX Hotels zurück · über qualifizierende Buchungen',
  },
  {
    topic: 'Autokredit',
    without: 'Zinsen von damals · Bank meldet sich nie · du zahlst ggf. Jahre zu viel',
    with: 'KI-Zins-Check · Angebot nur bei besseren Konditionen · direkt von der Bank',
  },
  {
    topic: 'Auto verkaufen',
    without: 'Händler drückt 10–25 % des Fahrzeugwerts ab · z. B. 3.500 € bei 20.000 € Auto',
    with: 'Concierge-Verkauf · 499 € statt 1.199 € · Marktpreis-Bewertung inklusive',
  },
  {
    topic: 'Ölwechsel',
    without: 'Ø 180 € alle 30.000 km · Werkstatt zieht extra Positionen dazu',
    with: 'Bis 450 € über FuelX Hotels gutschreibbar',
  },
  {
    topic: 'Fahrzeug-Diagnose',
    without: '„Warnleuchte an" → sofort Werkstatt · Ø 150–400 € Fehldiagnose',
    with: 'Werkstätten helfen vorab bei Einordnung — vor teuren Reparaturen',
  },
  {
    topic: 'Alles auf einer Rechnung',
    without: '10 Anbieter · 10 Buchungen · Chaos in der Steuererklärung',
    with: 'Alle Belege gebündelt · sauber archiviert · jederzeit im Dashboard verfügbar',
  },
];

const BUSINESS_ROWS = [
  {
    topic: 'Fuhrpark-Übersicht',
    without: 'Verträge auf 5 Systemen · 3 E-Mail-Postfächern · 3–6 h/Woche Verwaltung',
    with: 'Zentrale Kostenkontrolle in einem Dashboard · alle Fahrzeuge sichtbar',
  },
  {
    topic: 'Werkstatt-Rechnungen',
    without: 'Intransparent · Ø 15 % versteckte Aufschläge · schwer zu prüfen',
    with: 'Seriöse Werkstätten · Ersatzteil- + Arbeitspreise transparent',
  },
  {
    topic: 'Kfz-Versicherung Flotte',
    without: '20 Fahrzeuge × unklare Konditionen = schnell 5-stellige Zusatzkosten pro Jahr',
    with: 'KI-Vergleich Ihrer Verträge · Angebot direkt vom Versicherer bei Ersparnis',
  },
  {
    topic: 'Fahrzeugfinanzierungen',
    without: 'Konditionen von damals · nie überprüft · Zinsen oft 1,5–2 % über Marktpreis',
    with: 'KI-Analyse · Angebot direkt von der Dienstleister-Bank bei besseren Konditionen',
  },
  {
    topic: 'TÜV / HU (Skalierung)',
    without: '160 € × 50 Fzg = 8.000 € pro Zyklus · reine Prüfgebühren',
    with: 'Bis 160 € pro Fzg über FuelX Hotels zurück',
  },
  {
    topic: 'Reifen (2 Saisons)',
    without: '500 € × 50 Fzg = 25.000 € pro Jahr · fest im Budget',
    with: 'Bis 250 € pro Fzg pro Saison auf Prepaid Card',
  },
  {
    topic: 'Kfz-Steuer',
    without: 'Ø 300 € × 50 Fzg = 15.000 € pro Jahr · fix · nicht verhandelbar',
    with: 'Bis 450 € pro Fzg über FuelX Hotels zurück',
  },
  {
    topic: 'Ölwechsel & Wartung',
    without: 'Ø 450 € × 50 Fzg = 22.500 € pro Jahr · Werkstatt-Aufschläge inklusive',
    with: 'Bis 450 € pro Fzg über FuelX Hotels gutschreibbar',
  },
  {
    topic: 'Leasing-Rückgabe',
    without: 'Ø 800–1.500 € Nachbelastung pro Fahrzeug · Rückgabe-Überraschungen',
    with: 'Rückgabe-Vorbereitung · bis 500 € pro Fzg für Smart-Repair-Kosten',
  },
  {
    topic: 'Fahrzeug-Verkauf',
    without: 'Händlerankauf drückt 10–25 % Marge ab · Ø 3.500–8.000 € pro Fahrzeug',
    with: 'Concierge-Verkauf mit Marktpreis-Bewertung inklusive',
  },
  {
    topic: 'Geschäftsreisen',
    without: 'Portal-Hopping · Ø 45–60 Min pro Buchung · Spesen-Chaos in der Buchhaltung',
    with: '1,6 Mio Hotels · zentrale Sammelrechnung mit Kostenstellen-Zuordnung',
  },
  {
    topic: 'Mitarbeiter-Benefit',
    without: 'Corporate Benefits · einmal angeschaut, dann vergessen',
    with: 'Reisevorteile auch für private Buchungen · echter Bindungshebel',
  },
];

export default function ComparisonTable({ variant = 'private', testId = 'comparison-table' }) {
  const rows = variant === 'business' ? BUSINESS_ROWS : PRIVATE_ROWS;
  const brand = variant === 'business' ? 'FUEL.X Business' : 'FUEL.X Card';
  const eyebrow = variant === 'business' ? 'Fuhrpark-Vergleich' : 'Autofahrer-Vergleich';
  const headline = variant === 'business'
    ? 'Mit FUEL.X Business — oder ohne.'
    : 'Mit FUEL.X Card — oder ohne.';
  const sub = variant === 'business'
    ? 'Was ändert sich, wenn Sie Ihre Flottenkosten über eine Plattform bündeln? Zwölf konkrete Punkte im direkten Vergleich.'
    : 'Eine Plattform für alles rund ums Auto — Erinnerungen, Prämien-Rückholung, Werkstatt-Check, Hotelbuchung mit Bestpreis. Siebzehn konkrete Punkte im direkten Vergleich.';

  return (
    <section className="py-24 sm:py-32 px-6 bg-white border-y border-stone-200" data-testid={testId}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-14">
          <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-emerald-700 mb-4">{eyebrow}</p>
          <h2 className="font-display text-stone-900 text-3xl sm:text-4xl lg:text-[3.25rem] leading-[1.05] tracking-[-0.025em]">
            {headline}
          </h2>
          <p className="font-body-refined text-base sm:text-lg text-stone-500 leading-relaxed mt-5">
            {sub}
          </p>
        </div>

        {/* Info-Box: Erklärung wie im Konfigurator */}
        <div
          className="mb-10 max-w-3xl mx-auto rounded-2xl border border-emerald-200 bg-emerald-50/40 px-5 sm:px-7 py-5 flex items-start gap-4"
          data-testid={`${testId}-infobox`}
        >
          <ShieldCheck className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" strokeWidth={2} />
          <div className="text-[13px] sm:text-[14px] text-stone-700 leading-[1.6]">
            <p className="mb-1.5">
              <strong className="text-stone-900">So funktioniert{"'"}s:</strong>{' '}
              {variant === 'business'
                ? 'FuelGood Guthaben entsteht aus jedem gefahrenen Kilometer. Nutzen Sie FuelX Hotels für Buchungen — dann wird das Guthaben als Prepaid-Karten-Aufladung (Aral, Shell, BP, EnBW) übertragen und deckt Werkstatt, Reifen, TÜV und Kfz-Steuer ab. Durchschnittlich 0,40 € pro Liter Ersparnis.'
                : 'FuelGood Guthaben entsteht aus jedem gefahrenen Kilometer. Buche über FuelX Hotels — dann wird dein Guthaben als Prepaid-Karten-Aufladung (Aral, Shell, BP, EnBW) übertragen und du bezahlst damit Werkstatt, Reifen, TÜV, Kfz-Steuer. Durchschnittlich 0,40 € pro Liter Ersparnis.'}
            </p>
            <p className="text-stone-500">
              <strong className="text-stone-700">Wichtig zur Kfz-Versicherung & Autokredit:</strong>{' '}
              FUEL.X ist kein Makler, keine Bank und keine Versicherungsgesellschaft. Wir sind KI-basierter Leadvermittler — konkrete Angebote erhältst du{' '}
              <strong className="text-stone-700">nur bei tatsächlicher Ersparnis</strong> und immer{' '}
              <strong className="text-stone-700">direkt vom jeweiligen Versicherer bzw. der Bank</strong>. Wir beraten nicht.
            </p>
          </div>
        </div>

        {/* Table — Desktop: 3-Spalten-Grid | Mobile: gestapelt (Topic → 2 Karten) */}
        <div className="rounded-3xl border border-stone-200 overflow-hidden">
          {/* Head — nur Desktop */}
          <div className="hidden md:grid grid-cols-[1.2fr_1fr_1fr] text-[10px] uppercase tracking-[0.2em] font-semibold">
            <div className="bg-white px-5 sm:px-8 py-5 text-stone-400">Bereich</div>
            <div className="bg-stone-50 px-5 sm:px-8 py-5 text-stone-500 border-l border-stone-200">Ohne FuelX</div>
            <div className="bg-stone-900 px-5 sm:px-8 py-5 text-emerald-400 border-l border-stone-800">Mit {brand}</div>
          </div>

          {/* Rows */}
          {rows.map((row, i) => {
            const isLast = i === rows.length - 1;
            return (
              <div
                key={row.topic}
                className={!isLast ? 'border-b border-stone-100' : ''}
                data-testid={`${testId}-row-${row.topic.replace(/\s+/g, '-').toLowerCase()}`}
              >
                {/* Desktop 3-Spalten */}
                <div className="hidden md:grid md:grid-cols-[1.2fr_1fr_1fr] items-stretch">
                  <div className="bg-white px-5 sm:px-8 py-5 flex items-center">
                    <p className="text-[14px] sm:text-[15px] font-medium text-stone-900 leading-snug">{row.topic}</p>
                  </div>
                  <div className="bg-stone-50 px-5 sm:px-8 py-5 border-l border-stone-200 flex items-start gap-2.5">
                    <X className="w-4 h-4 text-stone-400 flex-shrink-0 mt-0.5" strokeWidth={2.2} />
                    <p className="text-[13px] text-stone-500 leading-snug">{row.without}</p>
                  </div>
                  <div className="bg-stone-900 text-white px-5 sm:px-8 py-5 border-l border-stone-800 flex items-start gap-2.5">
                    <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                    <p className="text-[13px] text-stone-200 leading-snug">{row.with}</p>
                  </div>
                </div>
                {/* Mobile — gestapelt */}
                <div className="md:hidden">
                  <p className="bg-white px-5 py-4 text-[13px] font-semibold text-stone-900 leading-snug border-b border-stone-100">{row.topic}</p>
                  <div className="bg-stone-50 px-5 py-4 flex items-start gap-2.5 border-b border-stone-100">
                    <div className="flex items-center gap-1.5 min-w-[76px] flex-shrink-0">
                      <X className="w-3.5 h-3.5 text-stone-400" strokeWidth={2.4} />
                      <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-stone-500">Ohne</span>
                    </div>
                    <p className="text-[12.5px] text-stone-500 leading-snug flex-1">{row.without}</p>
                  </div>
                  <div className="bg-stone-900 text-white px-5 py-4 flex items-start gap-2.5">
                    <div className="flex items-center gap-1.5 min-w-[76px] flex-shrink-0">
                      <Check className="w-3.5 h-3.5 text-emerald-400" strokeWidth={2.6} />
                      <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-emerald-400">Mit</span>
                    </div>
                    <p className="text-[12.5px] text-stone-200 leading-snug flex-1">{row.with}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footnote */}
        <p className="text-center text-[12px] text-stone-400 mt-6 leading-relaxed max-w-xl mx-auto flex items-center justify-center gap-1.5">
          <Info className="w-3 h-3 text-stone-400 flex-shrink-0" strokeWidth={2} />
          Werte &bdquo;bis zu&ldquo; — abhängig von qualifizierenden FuelX-Hotels-Buchungen und geltenden Bedingungen. Konkrete Freischaltgrenzen im Dashboard.
        </p>
      </div>
    </section>
  );
}
